import * as React from 'react';
import { post } from '@/lib/api';
import { useAuth } from '@/features/auth/AuthContext';
import { ONBOARDING_VERSION, TOUR, tourForPath } from './tours';

/**
 * Who has seen which tour, and which one is open right now.
 *
 * ### Where the "seen" flag lives, and why it is not `localStorage`
 *
 * On the **user**, server-side. The question is "has this person been shown how the
 * sections work?", not "has this browser?" — an accountant who signs in from the office
 * desktop and then a laptop has learned the system once, and being taught it again on
 * every device is what trains people to dismiss the dialog without reading it.
 *
 * ### Optimistic, and deliberately forgiving
 *
 * Closing the tour marks it seen locally *first* and tells the server afterwards. If that
 * request fails the worst case is the tour offering itself again next session, which is a
 * far better failure than a dialog that will not close because a POST timed out.
 */

const OnboardingContext = React.createContext(null);

export const OnboardingProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  /**
   * What the *server* says this person has seen — derived, never copied into state.
   *
   * This was a `useState` seeded by an effect, and it was wrong in a way only a test
   * caught: effects run after the render that reads them, so the very first pass saw an
   * empty set and opened the welcome tour over somebody who had finished it months ago.
   * A memo is correct on the first render, which is the only render that matters here
   * because the decision is made once and latched with `consideredRef`.
   *
   * A completion recorded against an older `version` is ignored rather than deleted:
   * "saw the old tour" and "never saw one" stay distinguishable.
   */
  const baseline = React.useMemo(() => {
    const stale = (user?.onboarding?.version ?? 0) < ONBOARDING_VERSION;
    return new Set(stale ? [] : (user?.onboarding?.completedTours ?? []));
  }, [user]);

  /** Optimistic changes made this session: `true` = seen, `false` = replayed. */
  const [overrides, setOverrides] = React.useState({});
  const [active, setActive] = React.useState(null);
  /** Set once the welcome tour has been considered, so it is offered at most once a session. */
  const consideredRef = React.useRef(false);

  const seen = React.useMemo(() => {
    const next = new Set(baseline);
    for (const [tourId, wasSeen] of Object.entries(overrides)) {
      if (wasSeen) next.add(tourId);
      else next.delete(tourId);
    }
    return next;
  }, [baseline, overrides]);

  // A different account — including a sign-out — starts over. Anything else would leave
  // the next person's dialogs suppressed by the last person's completions.
  React.useEffect(() => {
    setOverrides({});
    consideredRef.current = false;
    if (!user) setActive(null);
  }, [user]);

  const hasSeen = React.useCallback((tourId) => seen.has(tourId), [seen]);

  const record = React.useCallback((tourId, action) => {
    setOverrides((prev) => ({ ...prev, [tourId]: action !== 'reset' }));

    post('/auth/me/onboarding', { tour: tourId, action, version: ONBOARDING_VERSION }).catch(() => {
      /* A tour that offers itself again is a better failure than one that will not close. */
    });
  }, []);

  const start = React.useCallback((tourId) => setActive(tourId), []);

  const finish = React.useCallback(
    (tourId, action = 'complete') => {
      record(tourId, action);
      setActive((current) => (current === tourId ? null : current));
    },
    [record],
  );

  /** "Show me that again" — from the help menu. Clears the flag *and* opens it. */
  const replay = React.useCallback(
    (tourId) => {
      record(tourId, 'reset');
      setActive(tourId);
    },
    [record],
  );

  /**
   * Open the welcome tour on first login.
   *
   * Guarded by `consideredRef` rather than by the `seen` set alone: `finish` updates the
   * set optimistically, and without the ref a re-render between the local update and the
   * server round trip could re-open the tour the user just closed.
   */
  React.useEffect(() => {
    if (!isAuthenticated || !user || consideredRef.current) return;
    consideredRef.current = true;
    if (!seen.has(TOUR.WELCOME)) setActive(TOUR.WELCOME);
  }, [isAuthenticated, user, seen]);

  /**
   * Offer a screen's own tour the first time that screen is opened.
   *
   * Never while the welcome tour is up — two overlays at once is not onboarding, it is an
   * obstruction — and never as a *dialog*: screen tours are coach marks beside the thing
   * they describe, which is the whole reason they are separate from the welcome flow.
   */
  const offerScreenTour = React.useCallback(
    (pathname) => {
      if (active) return;
      const tour = tourForPath(pathname);
      if (!tour || seen.has(tour.id)) return;
      // The welcome tour comes first: a coach mark on a screen somebody has not been told
      // the purpose of explains the wrong layer.
      if (!seen.has(TOUR.WELCOME)) return;
      setActive(tour.id);
    },
    [active, seen],
  );

  const value = React.useMemo(
    () => ({ active, start, finish, replay, hasSeen, offerScreenTour, seen }),
    [active, start, finish, replay, hasSeen, offerScreenTour, seen],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

/**
 * Falls back to an inert implementation outside the provider so a component under test
 * renders rather than throwing. Onboarding is chrome — nothing depends on it working.
 */
const INERT = {
  active: null,
  seen: new Set(),
  start: () => {},
  finish: () => {},
  replay: () => {},
  hasSeen: () => true,
  offerScreenTour: () => {},
};

export const useOnboarding = () => React.useContext(OnboardingContext) ?? INERT;

export default OnboardingProvider;
